import type { AdminOrganizationResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatHours } from '@/lib/hours';
import { RecordConsultantHoursDialog } from './RecordConsultantHoursDialog';
import { ConsultantHoursTransactionsTable } from './ConsultantHoursTransactionsTable';

interface ConsultantHoursCardProps {
  organization: AdminOrganizationResponse;
}

/**
 * Shows an organization's consultant hours totals (assigned, spent, available), lets an admin
 * credit or debit hours, and lists the ledger of every credit and debit.
 */
export function ConsultantHoursCard({ organization }: ConsultantHoursCardProps) {
  const totals = [
    { label: 'Assigned', value: organization.consultantHoursAssigned },
    { label: 'Spent', value: organization.consultantHoursSpent },
    { label: 'Available', value: organization.consultantHoursAvailable },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Consultant Hours</CardTitle>
          <CardDescription>
            Hours credited to this organization and consumed against it
          </CardDescription>
        </div>
        <RecordConsultantHoursDialog
          organizationId={organization.id}
          availableHours={organization.consultantHoursAvailable}
        />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          {totals.map((total) => (
            <div key={total.label} className="rounded-md border p-4">
              <p className="text-sm font-medium text-muted-foreground">{total.label}</p>
              <p className="text-2xl font-semibold">{formatHours(total.value)}</p>
            </div>
          ))}
        </div>
        <ConsultantHoursTransactionsTable organizationId={organization.id} />
      </CardContent>
    </Card>
  );
}
